import React, { useState, useEffect, useContext } from 'react';
import { FormikTouched, FormikErrors } from 'formik';
import {
  Button,
  InlineNotification,
  InlineLoading,
  TextInput,
  TextArea,
  DropdownSkeleton,
  Select,
  SelectItem,
  SelectItemGroup
} from 'carbon-components-react';
import { ArrowLeft16, ArrowRight16 } from '@carbon/icons-react';
import { LocalStoreContext } from '../../../localStore/context';
import { LodgeEnqyiryInitialValues } from '../../../localStore/types';

const AuthorityReferenceForm: React.FC<Props> = ({
  goBack,
  goNext,
  values,
  handleBlur,
  handleChange,
  setFieldTouched,
  setFieldValue,
  redrawCount
}) => {
  const [isMultiUserRef, setIsMultiUserRef] = useState<boolean>(false);
  const [authorityDropDown, setAuthorityDropDown] = useState<
    any[] | undefined
  >();
  const [isOtherSelected, setIsOtherSelected] = useState<boolean>(false);
  const localStore = useContext(LocalStoreContext);
  const {
    shapes,
    authorityList,
    essentialsLoading,
    lodgingNewEnquiry,
    restriction,
    lodgeEnquiryError
  } = localStore;
  const { authorityType } = values;

  const translateErrorMessage = (errMsg: string) => {
    let translation = errMsg;
    if (errMsg === 'NOT_AUTHORIZED') {
      translation =
        'You are not authorised to create enquiry, please contact support@esriaustralia.com.au.';
    } else if (errMsg.includes('TypeError')) {
      translation =
        'You are not authorised to create enquiry, please contact support@esriaustralia.com.au.';
    }
    return translation;
  };

  useEffect(() => {
    if (authorityList && authorityList.length > 0) {
      const filtered = authorityList.filter((a: any) => {
        return a.authorityTypeId === authorityType;
      });

      setAuthorityDropDown(filtered.length > 0 ? filtered : undefined);

      if (authorityType === '1' || authorityType === '2') {
        const found = authorityList.filter((a: any) => {
          return (
            a.value === Number(values.authorityId) ||
            a.value === values.authorityId
          );
        });
        if (found.length === 0) {
          setFieldValue('authorityId', undefined);
        }
      }
      if (authorityType === '4') {
        setIsOtherSelected(true);
      }
    } else {
      if (authorityType === '4') {
        setIsOtherSelected(true);
      }
    }
  }, [authorityType, authorityList, setFieldValue, values.authorityId]);

  useEffect(() => {
    const shapeswithRef = shapes.filter((s) => s.userReference);
    setIsMultiUserRef(shapeswithRef.length > 0 && shapes.length > 1);
  }, [shapes]);

  useEffect(() => {
    if (typeof redrawCount !== 'undefined') {
      setFieldValue('authorityType', '');
      setFieldValue('authorityId', '');
      setFieldValue('otherAuthorityName', '');
    }
  }, [redrawCount, setFieldValue]);

  return (
    <div className="enquiry-form--authority">
      {lodgeEnquiryError && (
        <InlineNotification
          title={translateErrorMessage(lodgeEnquiryError) || ''}
          kind="error"
          lowContrast
        />
      )}
      <div className="form-header">
        <Button
          className="back-btn"
          kind="ghost"
          renderIcon={ArrowLeft16}
          iconDescription="Back"
          tooltipPosition="left"
          hasIconOnly
          onClick={() => goBack()}
          disabled={lodgingNewEnquiry}
        />
        <Button
          className="continue-btn"
          kind="secondary"
          renderIcon={!lodgingNewEnquiry ? ArrowRight16 : undefined}
          onClick={() => goNext()}
          disabled={
            !values.authorityType ||
            (authorityDropDown && !values.authorityId) ||
            (values.authorityType === '1' && !values.authorityId) ||
            (values.authorityType === '2' && !values.authorityId) ||
            (values.authorityType === '4' && !values.otherAuthorityName) ||
            (restriction &&
              restriction.total > 0 &&
              shapes.length + restriction.used > restriction.total) ||
            lodgingNewEnquiry
          }
        >
          {lodgingNewEnquiry ? (
            <InlineLoading description="Submitting..." />
          ) : (
            `Submit${shapes.length > 1 ? ` (${shapes.length})` : ''}`
          )}
        </Button>
      </div>
      <div className="form-body">
        <div className="form-input-section">
          {essentialsLoading ? (
            <DropdownSkeleton />
          ) : (
            <>
              <Select
                id="authorityType"
                labelText="Working on behalf of"
                value={
                  values.authorityType === '1' && values.authorityId
                    ? `1-${values.authorityId}`
                    : values.authorityType === '2' && values.authorityId
                    ? `2-${values.authorityId}`
                    : values.authorityType || ''
                }
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  if (val.startsWith('1-')) {
                    const authorityId = val.replace('1-', '');
                    setFieldTouched('authorityType', true);
                    setFieldValue('authorityType', '1');
                    setFieldValue('authorityId', authorityId);
                    setIsOtherSelected(false);
                    setFieldValue('otherAuthorityName', '');
                  } else if (val.startsWith('2-')) {
                    const authorityId = val.replace('2-', '');
                    setFieldTouched('authorityType', true);
                    setFieldValue('authorityType', '2');
                    setFieldValue('authorityId', authorityId);
                    setIsOtherSelected(false);
                    setFieldValue('otherAuthorityName', '');
                  } else if (val === '3' || val === '4') {
                    setFieldTouched('authorityType', true);
                    setFieldValue('authorityType', val);
                    setFieldValue('authorityId', undefined);
                    if (val === '4') {
                      setIsOtherSelected(true);
                    } else {
                      setIsOtherSelected(false);
                      setFieldValue('otherAuthorityName', '');
                    }
                  } else {
                    setFieldTouched('authorityType', true);
                    setFieldValue('authorityType', undefined);
                    setFieldValue('authorityId', undefined);
                    setIsOtherSelected(false);
                    setFieldValue('otherAuthorityName', '');
                  }
                }}
                disabled={essentialsLoading}
              >
                <SelectItem text="Choose an option" disabled value="" />
                <SelectItemGroup label="Local Authority">
                  {authorityList &&
                    authorityList
                      .filter((a: any) => a.authorityTypeId === '1')
                      .sort((a: any, b: any) => a.label.localeCompare(b.label))
                      .map((a: any) => (
                        <SelectItem
                          key={a.value}
                          value={`1-${a.value}`}
                          text={a.label}
                        />
                      ))}
                </SelectItemGroup>
                <SelectItemGroup label="Utility">
                  {authorityList &&
                    authorityList
                      .filter((a: any) => a.authorityTypeId === '2')
                      .sort((a: any, b: any) => a.label.localeCompare(b.label))
                      .map((a: any) => (
                        <SelectItem
                          key={a.value}
                          value={`2-${a.value}`}
                          text={a.label}
                        />
                      ))}
                </SelectItemGroup>
                <SelectItemGroup label="More">
                  <SelectItem text="Private" value="3" />
                  <SelectItem text="Other" value="4" />
                </SelectItemGroup>
              </Select>
            </>
          )}
          {isOtherSelected && (
            <div style={{ marginTop: '1rem' }}>
              <TextInput
                id="otherAuthorityName"
                name="otherAuthorityName"
                labelText="Other (name)"
                data-testid="otherAuthorityName"
                maxLength={100}
                onChange={handleChange}
                onBlur={handleBlur}
                onInput={() => setFieldTouched('otherAuthorityName', true)}
                value={values.otherAuthorityName}
              />
            </div>
          )}
        </div>
        <div className="form-input-section">
          {isMultiUserRef ? (
            <div className="bx--form-item bx--text-input-wrapper">
              <div className="bx--label">Your reference (optional)</div>
              <div className="multiple-user-reference">
                <span>Multiple references (Select shape to update)</span>
              </div>
            </div>
          ) : (
            <TextInput
              id="userReference"
              name="userReference"
              labelText="Your reference (optional)"
              placeholder="Brief reference to help identify this job"
              data-testid="userReference"
              maxLength={50}
              onChange={handleChange}
              onBlur={handleBlur}
              onInput={() => setFieldTouched('userReference', true)}
              value={values.userReference}
            />
          )}
          <TextArea
            id="notes"
            name="notes"
            labelText="Notes (optional)"
            placeholder="Share any special information about your job with asset owners"
            data-testid="notes"
            maxLength={500}
            onChange={handleChange}
            onBlur={handleBlur}
            onInput={() => setFieldTouched('notes', true)}
            value={values.notes}
          />
        </div>
      </div>
      <div className="form-footer">
        <div className="form-progress--wrapper">
          <div className="form-progress completed"></div>
          <div className="form-progress completed"></div>
          <div className="form-progress completed"></div>
        </div>
      </div>
    </div>
  );
};

type Props = {
  touched: FormikTouched<LodgeEnqyiryInitialValues>;
  errors: FormikErrors<LodgeEnqyiryInitialValues>;
  values: LodgeEnqyiryInitialValues;
  setFieldValue: Function;
  setFieldTouched: Function;
  handleChange: (e: React.ChangeEvent) => void;
  handleBlur: (e: React.FocusEvent) => void;
  goNext: Function;
  goBack: Function;
  redrawCount?: number;
};

export default React.memo(AuthorityReferenceForm);
